import { useTranslation } from "react-i18next";

import { Box, ButtonBase } from "@mui/material";
import { alpha } from "@mui/material/styles";

import { FS } from "@plannel/utils/font-util";

/*
 * 다중선택 토큰(알약) 그리드 — 설정 모달 본문에서 **체크박스 목록을 대신한다**.
 *
 * 왜 체크박스가 아닌가: 계층 레벨·Code·ABC-XYZ·속성 10개를 체크박스 행으로 세우면 모달이 세로로
 * 길어져 스크롤이 생긴다(DimensAttrModal 이 실제로 attr 를 2열로 접어 버티고 있었다).
 * 토큰은 라벨 길이만큼만 차지하고 가로로 흘러 접히므로 같은 높이에 2~3배가 들어간다.
 *
 *   <TokenSelectGroup
 *     label={t("attr")}                                  // 이미 번역된 섹션 제목
 *     options={[{ value: "attr01", label: "Weight" }]}   // label 도 번역된 문자열
 *     value={["attr01"]}                                 // 선택된 value 배열
 *     onChange={next => setAttr(next)}                   // 새 배열(선택 순서 유지)
 *   />
 *
 * props
 *   label         : 섹션 제목. 없으면 헤더 줄 자체를 생략(전체선택도 같이 사라진다)
 *   options       : [{ value, label, disabled? }] — value 는 문자열/숫자(키로도 쓴다)
 *   value         : 선택된 value 배열
 *   onChange      : (nextValues) => void. 선택은 뒤에 붙고 해제는 걸러낸다 — **순서가 보존**된다
 *                   (정렬이 필요하면 호출측에서 한다. 저장 형식이 화면마다 다르다)
 *   disabled      : 섹션 전체 잠금(전체선택 링크 포함). 개별 잠금은 option.disabled
 *   showSelectAll : 기본 true. 항목이 하나뿐인 섹션(Code, ABC-XYZ)은 false — 링크가 토큰과 같은 일을 한다
 *   sx            : 바깥 Box 에 얹을 여백 등
 *
 * ⚠ Typography 를 쓰지 않는다 — 전역 SCSS(.content-main .MuiTypography-root)가 font-size 를
 *   덮어써서 설정 화면 안에서만 글자가 커진다(StatPill 이 같은 이유로 Box 를 쓴다).
 */

// 섹션 제목 — 작은 트래킹 대문자. 토큰이 시각적으로 무거워 제목은 최대한 가볍게 둔다
const SECTION_LABEL_SX = {
  fontSize: FS.CAPTION,
  fontWeight: 600,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "text.secondary",
  lineHeight: 1,
};

const TOKEN_H = 34;

const TokenSelectGroup = ({ label, options = [], value = [], onChange, disabled = false, showSelectAll = true, sx }) => {
  const { t } = useTranslation();

  const selectable = options.filter(option => !option.disabled);
  const isOn = optionValue => value.includes(optionValue);
  const allOn = selectable.length > 0 && selectable.every(option => isOn(option.value));

  const toggle = optionValue => {
    onChange(isOn(optionValue) ? value.filter(v => v !== optionValue) : [...value, optionValue]);
  };

  // 전체선택/해제 — 잠긴 항목은 건드리지 않는다(잠긴 채 켜져 있던 값은 그대로 남는다)
  const toggleAll = () => {
    const lockedOn = value.filter(v => !selectable.some(option => option.value === v));
    onChange(allOn ? lockedOn : [...lockedOn, ...selectable.map(option => option.value)]);
  };

  return (
    <Box sx={{ mb: 1.5, ...sx }}>
      {label && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, pb: 1 }}>
          <Box sx={SECTION_LABEL_SX}>{label}</Box>
          {showSelectAll && selectable.length > 0 && (
            <ButtonBase
              disabled={disabled}
              onClick={toggleAll}
              sx={{
                fontSize: FS.CAPTION, fontWeight: 500, lineHeight: 1,
                px: 0.75, py: 0.5, borderRadius: 1, color: "primary.main",
                "&:hover": { bgcolor: theme => alpha(theme.palette.primary.main, 0.08) },
                "&.Mui-disabled": { color: "text.disabled" },
              }}>
              {allOn ? t("clearAll") : t("selectAll")}
            </ButtonBase>
          )}
        </Box>
      )}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {options.map(option => {
          const on = isOn(option.value);
          return (
            <ButtonBase
              key={option.value}
              disabled={disabled || option.disabled}
              onClick={() => toggle(option.value)}
              aria-pressed={on}
              sx={{
                boxSizing: "border-box",
                display: "inline-flex", alignItems: "center", gap: 1,
                height: TOKEN_H, px: 1.625, borderRadius: "9px",
                fontSize: FS.TITLE, fontWeight: 500, lineHeight: 1,
                color: on ? "primary.dark" : "text.secondary",
                bgcolor: theme => (on ? alpha(theme.palette.primary.main, 0.1) : theme.palette.grey[100]),
                border: 1,
                borderColor: theme => (on ? alpha(theme.palette.primary.main, 0.4) : "transparent"),
                transition: "background-color .14s, border-color .14s, color .14s",
                "&:hover": {
                  bgcolor: theme => (on ? alpha(theme.palette.primary.main, 0.16) : theme.palette.grey[200]),
                },
                // 위 color/bgcolor 가 MUI 기본 Mui-disabled 를 덮으므로 명시한다(안 쓰면 잠긴 게 안 보인다)
                "&.Mui-disabled": { color: "text.disabled", bgcolor: "grey.100", borderColor: "transparent", opacity: 0.7 },
              }}>
              {/* 선택 표시 점 — 토큰이 켜졌는지 색만으로 판단하지 않게 한다 */}
              <Box sx={{
                width: 6, height: 6, borderRadius: "50%", flexShrink: 0,
                bgcolor: on ? "primary.main" : "grey.400",
                transition: "background-color .14s",
              }} />
              {option.label}
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
};

export default TokenSelectGroup;
